/**
 * Display-only translation overlay (translations v1).
 *
 * The calculator payload may carry `translations: { locale, map }` for the
 * shopper's `window.Shopify.locale`. `t(source)` swaps a shopper-VISIBLE
 * string for its translation and is identity for anything unknown, so a store
 * with no translations renders byte-identical markup to today.
 *
 * Iron rule: `t()` is applied to what a shopper READS — label text, option
 * text, placeholders, tooltips, validation and dialog copy, and the cart line
 * attributes the shopper sees in the cart, checkout and order. It is NEVER
 * applied to `name="properties[...]"`, `data-label`, `data-val`, `data-calcname`
 * or the `POST variant` body: those are pricing keys (FormulaService matches
 * element identifiers AND option labels) and must stay canonical English.
 */
import { appState } from "../state/app-state";

// The blocks that read right-to-left: Hebrew, Arabic (and its supplements and
// presentation forms), Syriac, Thaana, NKo, Samaritan, Mandaic.
const STRONG_RTL = /[֐-׿؀-ۿ܀-ݏݐ-ݿހ-޿߀-߿ࠀ-࡟יִ-ﭏﭐ-﷿ﹰ-﻿]/;
// Latin, Greek and Cyrillic — enough to recognise a left-to-right phrase. Anything
// matching neither (digits, punctuation, spaces, symbols) is neutral and skipped.
const STRONG_LTR = /[A-Za-zÀ-ʸͰ-֏]/;

/**
 * Does this phrase START right-to-left? Returns null for a phrase with no strong
 * character at all ("45", "-", "%"), so it neither votes nor counts.
 */
function startsRtl(text: string): boolean | null {
  for (const ch of text) {
    if (STRONG_RTL.test(ch)) return true;
    if (STRONG_LTR.test(ch)) return false;
  }
  return null;
}

/**
 * Is the language we are serving a right-to-left one? Decided by majority vote of
 * the translated phrases rather than from a list of locale codes, for the same
 * reason `dir="auto"` is used on the phrases themselves: a locale list of ours
 * would drift out of step with Shopify's. A map that is mostly Hebrew wins even
 * when a few entries were left in English, and a map of digits decides nothing.
 */
function mapReadsRtl(map: Record<string, string>): boolean {
  let rtl = 0;
  let ltr = 0;
  for (const key in map) {
    const vote = startsRtl(map[key]!);
    if (vote === true) rtl++;
    else if (vote === false) ltr++;
  }
  return rtl > ltr;
}

export function setTranslations(map: Record<string, string> | null | undefined): void {
  const usable =
    map && typeof map === "object" && Object.keys(map).length > 0 ? map : null;
  appState.cpc_translations = usable;
  // Computed once here rather than per element: every builder asks, and the answer
  // cannot change without a new payload.
  appState.cpc_translations_rtl = usable !== null && mapReadsRtl(usable);
}

export function hasTranslations(): boolean {
  return appState.cpc_translations !== null;
}

/** True when the phrases we are serving read right-to-left. */
export function isRtl(): boolean {
  return appState.cpc_translations_rtl;
}

/**
 * ` dir='rtl'` for the calculator container when we are serving a right-to-left
 * language, and "" otherwise.
 *
 * Marking each phrase with `dir="auto"` fixes the direction of the TEXT but not
 * of the LAYOUT: a Hebrew label right-aligns inside a container that is still
 * left-to-right, so the label sits hard against the right edge while its input
 * stays on the left and nothing lines up (what Shai saw on QA). Direction has to
 * be set on the block for the whole calculator to mirror as a unit. The per-phrase
 * `dir="auto"` still earns its keep inside an RTL container: it is what keeps a
 * phrase the merchant left in English reading left-to-right.
 */
export function containerDirAttr(): string {
  return isRtl() ? " dir='rtl'" : "";
}

/** The translation of `source`, or `source` itself when there is none. */
export function t(source: any): any {
  const map = appState.cpc_translations;
  if (map === null || typeof source !== "string") return source;
  const hit = map[source];
  return typeof hit === "string" && hit.length > 0 ? hit : source;
}

/**
 * The shopper's locale as Shopify's theme global reports it ("fr", "pt-BR").
 * Absent outside a storefront (builder preview, a theme without the global),
 * in which case no locale is sent and no translation is served.
 */
export function shopperLocale(): string | null {
  const locale = (window as any).Shopify?.locale;
  return typeof locale === "string" && locale.length > 0 ? locale : null;
}

/**
 * Cart line attributes (`_cpc_properties`, the native form's `properties[...]`)
 * are what the shopper sees in the cart, checkout and order, so both the key
 * (the element name) and a choice value (an option label, "Checked") are
 * swapped. Numbers, typed text and upload URLs pass through `t()` unchanged
 * because no translation row exists for them. Lookup axes carry their labels
 * inside an object and are translated in place.
 */
export function translateCartProperties(props: Record<string, any>): Record<string, any> {
  if (!hasTranslations()) return props;
  const out: Record<string, any> = {};
  for (const key of Object.keys(props)) {
    const value = props[key];
    if (value && typeof value === "object" && "input1_label" in value) {
      out[t(key)] = {
        ...value,
        input1_label: t(value.input1_label),
        input2_label: t(value.input2_label),
      };
    } else {
      out[t(key)] = typeof value === "string" ? t(value) : value;
    }
  }
  return out;
}

/**
 * The redirect submitter posts a FormData built from the product form (plus
 * the App Block collection). Its `properties[...]` entries are the cart line's
 * visible attributes, so keys and choice values are swapped here — after every
 * other rewrite, right before the post. Underscore-prefixed entries (hidden
 * from the shopper, incl. the `_cpc_properties` blob) are left alone; files
 * pass through untouched.
 */
export function translateFormData(formData: FormData): void {
  if (!hasTranslations()) return;
  const keys: string[] = [];
  (formData as any).forEach((_value: unknown, key: string) => {
    if (keys.indexOf(key) === -1) keys.push(key);
  });
  const moves: Array<{ from: string; to: string; values: FormDataEntryValue[] }> = [];
  keys.forEach((key) => {
    if (!key.startsWith("properties[") || key.startsWith("properties[_")) return;
    const values = formData.getAll(key);
    const to = translatePropertyName(key);
    const translated = values.map((v) => (typeof v === "string" ? t(v) : v));
    const changed = to !== key || translated.some((v, i) => v !== values[i]);
    if (changed) moves.push({ from: key, to, values: translated });
  });
  moves.forEach((m) => formData.delete(m.from));
  moves.forEach((m) => m.values.forEach((v) => formData.append(m.to, v as any)));
}

/** `properties[Width]` → `properties[Largeur]`; anything else is returned as-is. */
export function translatePropertyName(name: string): string {
  if (!hasTranslations() || !name.startsWith("properties[") || !name.endsWith("]")) return name;
  const inner = name.slice("properties[".length, -1);
  // Underscore-prefixed properties are hidden from the shopper by Shopify — never translated.
  if (inner.startsWith("_")) return name;
  const translated = t(inner);
  return translated === inner ? name : "properties[" + translated + "]";
}
